import type { MetricWithAttribution } from "web-vitals/attribution";
import { trackEvent } from "../functions/analytics";
import { claritySet } from "./clarity";

// Flag REACT_APP_WEB_VITALS_ENABLED (DESIGN-CLARITY §7.2 Q5). Field numbers
// for LCP / INP / CLS go out as ONE GA4 event per metric through the
// existing trackEvent facade (which drops everything while the analytics
// are stopped: Decline, no-analytics routes, internal users) and, on a
// "poor" rating, as the Clarity tag cwv_daarlig=<metric> so bad sessions
// can be filtered in Recordings. trackEvent only sends when gtag.js is
// loaded (= after Accept with the consent flag on), so a metric that
// resolves before consent is simply lost - the honest bias: consenting
// sessions only. Nothing here throws and nothing runs at module level.
//
// Honest SPA limit (web-vitals@4 defaults): LCP is reported ONLY for the
// hard navigation, and CLS / INP once per page lifetime (at pagehide /
// visibility hidden), not per route. page_path is therefore the route the
// user LEFT the app from; landing_path (captured when index.tsx schedules
// the measurement, before any SPA redirect) is the route the LCP belongs
// to. Read LCP per landing_path and filter on navigation_type in GA4.

export const WEB_VITALS_EVENT = "web_vitals";

// Set once by scheduleWebVitals (= app start); undefined until then.
let landingPath: string | undefined;

// The attribution field that names the responsible element differs per
// metric; an empty string when the library has none (e.g. INP on a removed
// element). Selectors only - never text content.
export function metricElement(metric: MetricWithAttribution): string {
  try {
    switch (metric.name) {
      case "LCP":
        return metric.attribution.element ?? "";
      case "CLS":
        return metric.attribution.largestShiftTarget ?? "";
      case "INP":
        return metric.attribution.interactionTarget ?? "";
      default:
        return "";
    }
  } catch {
    return "";
  }
}

// LCP/INP are milliseconds (rounded to whole ms); CLS is unitless and kept
// to three decimals so a 0.1 threshold stays readable in GA4 Explore.
export function metricValue(metric: MetricWithAttribution): number {
  return metric.name === "CLS"
    ? Math.round(metric.value * 1000) / 1000
    : Math.round(metric.value);
}

export function reportWebVital(metric: MetricWithAttribution): void {
  try {
    trackEvent(WEB_VITALS_EVENT, {
      metric_name: metric.name,
      value: metricValue(metric),
      rating: metric.rating,
      page_path: window.location.pathname,
      landing_path: landingPath ?? window.location.pathname,
      element: metricElement(metric),
      navigation_type: metric.navigationType,
      metric_id: metric.id,
    });
    if (metric.rating === "poor") {
      claritySet("cwv_daarlig", metric.name);
    }
  } catch {
    // Analytics must never break the app.
  }
}

// Runs the (lazy) web-vitals import off the critical path: on browser idle
// when available (bounded so it still happens on a busy page), else on the
// next macrotask. web-vitals reads the buffered performance entries, so
// starting after the first render misses nothing. A start that throws or
// returns a rejecting promise (e.g. the web-vitals chunk 404s after a
// deploy while the tab is open - lazyWithRetry only covers routes) is
// swallowed: no unhandled rejection, the app is already rendered.
export function scheduleWebVitals(start: () => void | Promise<void>): void {
  try {
    landingPath = window.location.pathname;
    const run = () => {
      try {
        const result = start();
        if (result && typeof (result as Promise<void>).catch === "function") {
          (result as Promise<void>).catch(() => {
            // Measurement is best-effort.
          });
        }
      } catch {
        // Measurement is best-effort.
      }
    };
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(run, { timeout: 3000 });
    } else {
      window.setTimeout(run, 0);
    }
  } catch {
    // Ignore - measurement is best-effort.
  }
}
